import { Singleton } from '@skylab-shared/utils';
import { CacheFactory } from 'cachefactory';
import { v4 as uuid } from 'uuid';
import { ConsoleLogger } from '../../logger';
import { DataProvider } from './../../context';

interface DataProviderDescriptor<T> {
    id: string;
    func: DataProvider<T>;
    maxAge: number;
}

@Singleton
export class CacheStorage {
    private readonly cacheFactory: CacheFactory;

    private readonly logger: ConsoleLogger;
    private readonly storageId: string;
    private readonly cache: any;
    private dataProviders: {
        [id: string]: DataProviderDescriptor<any>
    };
    private pendingPromises: { [id: string]: Promise<any> };

    //#region Constructor

    public constructor() {
        this.cacheFactory = new CacheFactory();
        this.logger = new ConsoleLogger(`${this.constructor.name}`);

        this.storageId = uuid();
        this.dataProviders = {};
        this.pendingPromises = {};

        if (!this.cache) {
            this.cache = this.createCache();
        }
    }

    //#endregion

    //#region Public methods
    public async addAsync<T>(id: string, dataProvider: DataProvider<T>, maxAge: number = Number.MAX_VALUE): Promise<void> {
        if (this.has(id))
            throw new Error(`${id} already exists in context.`);

        const dataProviderDescriptor = { id: id, func: dataProvider, maxAge };

        await this.addToCacheAsync(dataProviderDescriptor);
    }

    public get<T>(id: string): T {
        if (this.pendingPromises.hasOwnProperty(id))
            throw new Error(`Data not yet loaded (id:${id}).`);
        if (!this.has(id))
            throw new Error(`${id} does not exists in context.`);
        return this.cache.get(id);
    }

    public async getAsync<T>(id: string): Promise<T> {
        if (this.pendingPromises.hasOwnProperty(id)) {
            return this.pendingPromises[id];
        }

        const data = this.cache.get(id);

        return Promise.resolve(data);
    }

    public has(id: string): boolean {
        return this.dataProviders.hasOwnProperty(id);
    }

    public remove(id: string): void {
        if (!this.has(id))
            throw new Error(`${id} does not exists in context.`);

        delete this.dataProviders[id];
        this.cache.remove(id);
    }

    public async refresh(id: string): Promise<void> {
        if (!this.has(id))
            throw new Error(`${id} does not exists in context.`);

        await this.refreshCacheAsync(id);
    }

    public clear(): void {
        this.dataProviders = {};
        this.cache.removeAll();
    }

    public hasKey(key: string): boolean {
        return this.cache.keys().includes(key);
    }
    //#endregion

    //#region Private methods

    private createCache(): any {
        return this.cacheFactory.createCache(this.storageId, {
            maxAge: Number.MAX_VALUE,
            recycleFreq: 1000 * 5,
            onExpire: key => {
                this.logger.info(`${key} (data expired)`);
                this.refreshCacheAsync(key);
            }
        });
    }

    private async addToCacheAsync<T>(dataProviderDescriptor: DataProviderDescriptor<T>): Promise<void> {
        const key = dataProviderDescriptor.id;
        this.dataProviders[key] = dataProviderDescriptor;
        await this.refreshCacheAsync(key);
    }

    private async refreshCacheAsync(key: string): Promise<void> {
        await this.getRefreshPromise(key);
    }

    private getRefreshPromise(id: string): Promise<any> {
        this.cache.remove(id);
        const promise = this.dataProviders[id].func();
        this.pendingPromises[id] = promise.then(data => {
            delete this.pendingPromises[id];

            this.cache.put(id, data, { maxAge: this.dataProviders[id].maxAge });
            this.logger.info(`${id} (data added to cache)`);

            return data;
        }).catch(() => {
            delete this.pendingPromises[id];
        });
        return promise;
    }
    //#endregion
}

export const CacheStorageService = new CacheStorage();
